import { notification } from '@surecompanies/core_components'

export enum PopupMsgTypes {
  DETAIL_POPUP = 'detail-popup-result',
}

type DetailPopupMessage = {
  type: PopupMsgTypes
  isSuccess?: boolean
  message?: string
  description?: string
}

const NOTIFICATION_KEY = 'detail-popup-notification'

/**
 * Validates an incoming `MessageEvent` posted by a detail popup window and
 * displays the appropriate notification.
 *
 * The function is a no-op unless **all** of the following conditions hold:
 * - `event.origin` matches the provided `origin`
 * - `event.data` is a non-null object
 * - `event.data.type` equals `PopupMsgTypes.DETAIL_POPUP`
 * - `event.data.message` is a non-empty string
 *
 * When the message signals success (`isSuccess: true`), a success notification
 * is shown and `onRefresh` is called.
 * When the message signals failure, an error notification is shown (with an
 * optional `description`).
 *
 * @param event - The `MessageEvent` received on the `"message"` window event.
 * @param origin - The expected origin (e.g. `window.location.origin`). Events
 *   from any other origin are silently ignored.
 * @param onRefresh - Callback invoked after a successful popup action so the
 *   parent page can refresh its data.
 *
 * @example
 * // React hook – register inside useEffect
 * useEffect(() => {
 *   const messageHandler = (event: MessageEvent) => {
 *     handlePopupMessageListener({
 *       event,
 *       origin: window.location.origin,
 *       onRefresh: () => refetch(),
 *     })
 *   }
 *   window.addEventListener('message', messageHandler)
 *   return () => window.removeEventListener('message', messageHandler)
 * }, [])
 *
 * @example
 * // Class component – attach in componentDidMount, clean up in componentWillUnmount
 * componentDidMount() {
 *   this.messageHandler = (event: MessageEvent) => {
 *     handlePopupMessageListener({
 *       event,
 *       origin: window.location.origin,
 *       onRefresh: this.refreshList.bind(this),
 *     })
 *   }
 *   window.addEventListener('message', this.messageHandler)
 * }
 *
 * componentWillUnmount() {
 *   if (this.messageHandler) {
 *     window.removeEventListener('message', this.messageHandler)
 *   }
 * }
 */

export const handlePopupMessageListener = ({
  event,
  origin,
  onRefresh,
}: {
  event: MessageEvent
  origin: string
  onRefresh: () => void
}) => {
  if (event.origin !== origin) return
  if (typeof event.data !== 'object' || event.data === null) return
  const data = event.data as DetailPopupMessage
  if (data.type !== PopupMsgTypes.DETAIL_POPUP || !data.message) return
  if (data.isSuccess) {
    notification.success({
      key: NOTIFICATION_KEY,
      message: data.message,
      duration: 5,
    })
    onRefresh()
  } else {
    notification.error({
      key: NOTIFICATION_KEY,
      message: data.message,
      description: data.description,
      duration: 5,
    })
  }
}
